import type { FieldError, FieldErrors, RegisterOptions, SubmitHandler, UseFormRegister } from 'react-hook-form';
import {
  Box,
  Button,
  FormField,
  Heading,
  Input,
  Label,
  Modal,
  Paragraph,
  Spinner,
  Text,
  Textarea,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import {
  useRentalManagerUser,
  useRentalManagerUserClaimableListingsWebApiHandler_getUserSalesProInfo,
  useRentalManagerUserClaimableListingsWebApiHandler_sendClaimingSalesMessage,
} from '@zg-rentals/rental-manager-env';

import { styled } from '@/styled-system/jsx';
import talkToSalesImage from '../../images/talk-to-sales.png';
import { useForm } from 'react-hook-form';
import { useState } from 'react';

type TalkToSalesFormInputs = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
};

const TalkToSalesHeaderContainer = styled(Box, {
  base: {
    backgroundColor: 'blue.100',
    paddingInline: '400',
    display: 'flex',
    margin: '-400',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: 'lg lg 0 0',
    '& img': {
      width: '148px',
      height: '148px',
    },
    md: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
  },
});

const TalkToSalesText = styled(Box, {
  base: {
    flexGrow: 1,
    padding: 'xl',
    paddingLeft: 0,
  },
});

const TalkToSalesHeader = () => (
  <TalkToSalesHeaderContainer>
    <TalkToSalesText>
      <Heading level={4} css={{ fontFamily: 'serif' }}>
        Let us help you reach more renters
      </Heading>
      <Paragraph>
        Tell us what you&apos;re interested in, and we&apos;ll follow up with the right strategy to market your property
        and reach your portfolio goals.
      </Paragraph>
    </TalkToSalesText>
    <img src={talkToSalesImage} alt="Talk to sales" />
  </TalkToSalesHeaderContainer>
);

const ModalInput = ({
  error,
  defaultValue,
  fieldName,
  register,
  label,
  options,
  useTextArea = false,
}: {
  error?: FieldError;
  defaultValue: string;
  fieldName: keyof TalkToSalesFormInputs;
  register: UseFormRegister<TalkToSalesFormInputs>;
  label: string;
  options?: RegisterOptions<TalkToSalesFormInputs>;
  useTextArea?: boolean;
}) => {
  if (options?.required) {
    options.required = `${label} is required`;
  }

  return (
    <FormField>
      <Label>{label}</Label>
      {useTextArea ? (
        <Textarea error={!!error} defaultValue={defaultValue} {...register(fieldName, options)} />
      ) : (
        <Input error={!!error} defaultValue={defaultValue} {...register(fieldName, options)} />
      )}
      {error && (
        <Text textStyle="body-xs-bold" css={{ color: 'red.500' }}>
          {error.message}
        </Text>
      )}
    </FormField>
  );
};

const ModalBody = ({
  register,
  errors,
  renderBodyHeader,
  defaultMessage,
}: {
  register: UseFormRegister<TalkToSalesFormInputs>;
  errors: FieldErrors<TalkToSalesFormInputs>;
  renderBodyHeader?: boolean;
  defaultMessage: string;
}) => {
  const { data: salesInfo, isLoading: loadingSalesInfo } =
    useRentalManagerUserClaimableListingsWebApiHandler_getUserSalesProInfo();
  const { user } = useRentalManagerUser();

  if (loadingSalesInfo)
    return (
      <Box css={{ display: 'flex', justifyContent: 'center', padding: 'md' }}>
        <Spinner size="lg" />
      </Box>
    );

  return (
    <>
      {renderBodyHeader && <TalkToSalesHeader />}
      <Box css={{ padding: 'sm' }}>
        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'sm' }}>
          <Box css={{ gridColumn: { base: 'span 2', md: 'span 1' } }}>
            <ModalInput
              defaultValue={user.firstName ?? ''}
              register={register}
              fieldName="firstName"
              label="First name"
              error={errors.firstName}
              options={{ required: true }}
            />
          </Box>
          <Box css={{ gridColumn: { base: 'span 2', md: 'span 1' } }}>
            <ModalInput
              defaultValue={user.lastName ?? ''}
              register={register}
              fieldName="lastName"
              label="Last name"
              error={errors.lastName}
              options={{ required: true }}
            />
          </Box>
          <Box css={{ gridColumn: { base: 'span 2', md: 'span 1' } }}>
            <ModalInput
              defaultValue={user.email ?? ''}
              register={register}
              fieldName="email"
              label="Email"
              error={errors.email}
              options={{
                required: true,
                pattern: {
                  value: /[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+$/i,
                  message: 'Please enter a valid email',
                },
              }}
            />
          </Box>
          <Box css={{ gridColumn: { base: 'span 2', md: 'span 1' } }}>
            <ModalInput
              defaultValue=""
              register={register}
              fieldName="phone"
              label="Phone"
              error={errors.phone}
              options={{
                required: true,
                pattern: {
                  value: /^([+]?[0-9]{1,3})?[0-9]{10}$/i,
                  message: 'Please enter a valid phone number',
                },
              }}
            />
          </Box>
          <Box css={{ gridColumn: 'span 2' }}>
            <ModalInput
              defaultValue={defaultMessage}
              register={register}
              fieldName="message"
              label="Message"
              error={errors.message}
              options={{ required: true }}
              useTextArea
            />
          </Box>
        </Box>
        {salesInfo && salesInfo.length > 0 && (
          <Box css={{ marginTop: 'sm' }}>
            <Text textStyle="body-sm-bold">Your message will be sent to</Text>
            <Box css={{ marginBlock: 'sm' }}>
              <Paragraph>
                {salesInfo[0].firstName} {salesInfo[0].lastName}
              </Paragraph>
              <Paragraph>{salesInfo[0].email}</Paragraph>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

const ModalFooter = ({
  onClose,
  onSubmit,
  sendingClaimSalesMessage,
}: {
  onClose: () => void;
  onSubmit: () => void;
  sendingClaimSalesMessage: boolean;
}) => (
  <>
    <Button tone="neutral" emphasis="outlined" css={{ marginRight: 'md' }} onClick={onClose}>
      Cancel
    </Button>
    <Button tone="brand" emphasis="filled" type="submit" onClick={onSubmit} disabled={sendingClaimSalesMessage}>
      {sendingClaimSalesMessage ? 'Submitting...' : 'Submit'}
    </Button>
  </>
);

export const TalkToSalesModal = ({
  onOpen,
  renderBodyHeader = true,
  title,
  buttonText = 'Talk to sales',
  defaultMessage = "I'm interested in learning more about advertising my properties.",
  isFromClaimPathBanner = false,
}: {
  onOpen?: () => void;
  renderBodyHeader?: boolean;
  title?: string;
  buttonText?: string;
  defaultMessage?: string;
  isFromClaimPathBanner?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TalkToSalesFormInputs>();
  const { enqueueToast } = useToast();
  const { mutate: sendClaimingSalesMessage, isPending: sendingClaimSalesMessage } =
    useRentalManagerUserClaimableListingsWebApiHandler_sendClaimingSalesMessage({
      onError: () => {
        enqueueToast(<Toast tone="critical">Failed to send message.</Toast>);
      },
      onSuccess: () => {
        enqueueToast(<Toast tone="success">Your message has been sent successfully.</Toast>);
        setIsOpen(false);
      },
    });

  const onSubmit: SubmitHandler<TalkToSalesFormInputs> = (data: TalkToSalesFormInputs) => {
    sendClaimingSalesMessage({
      postData: {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        message: data.message,
        phoneNumber: data.phone,
      },
    });
  };

  return (
    <>
      <TextButton
        tone="brand"
        css={{ marginLeft: { base: 0, lg: isFromClaimPathBanner ? '0' : '100' }, display: 'inline-block' }}
        onClick={() => {
          setIsOpen(true);
          onOpen?.();
        }}
      >
        {buttonText}
      </TextButton>

      <Modal
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) setIsOpen(false);
        }}
        size={{ base: 'fullScreen', lg: 'md' }}
        header={title || undefined}
        body={
          <ModalBody
            register={register}
            errors={errors}
            renderBodyHeader={renderBodyHeader}
            defaultMessage={defaultMessage}
          />
        }
        footer={
          <ModalFooter
            onClose={() => setIsOpen(false)}
            onSubmit={handleSubmit(onSubmit)}
            sendingClaimSalesMessage={sendingClaimSalesMessage}
          />
        }
      />
    </>
  );
};
